import { useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { useWebSocket } from '../context/WebSocketContextUtil';
import { useActiveContext } from '../hooks/useActiveContext';
import { cn } from '@/lib/utils';
import { decodeOutput, encodeBinaryInput, encodeInput } from '@/lib/terminalBytes';
import { TerminalStream, type TerminalFrameMessage } from '@/lib/terminalStream';
import type { StreamHeader } from '@/lib/reliableStream';

// The gateway closes a terminal session after this long with nothing sent
// either way (its proxy manager's inactivity timeout).
const IDLE_CLOSE_MS = 300_000;
// How long before that close the header warns of it.
const IDLE_WARNING_MS = 30_000;
// `PROXY_SESSION_ERROR` in gateway-api.proto's `ProxySessionEventType`: the
// session failed. A session the device ended, or that idled out, didn't.
const PROXY_SESSION_ERROR = 3;
// What a gateway that doesn't say opens the device's terminal at.
const DEFAULT_COLS = 120;
const DEFAULT_ROWS = 24;

interface DeviceTerminalProps {
  deviceId: string;
}

type ConnectionState = 'connecting' | 'connected' | 'disconnected';

export default function DeviceTerminal({ deviceId }: DeviceTerminalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  // Seconds until the idle close, once it is near enough to warn of.
  const [idleSecondsLeft, setIdleSecondsLeft] = useState<number | null>(null);
  // Bumped by Reconnect to re-run the connection effect.
  const [attempt, setAttempt] = useState(0);
  const { isConnected, sendRaw, addRawListener } = useWebSocket();
  const { activeContextId } = useActiveContext();

  // Each connected (device, attempt) is a new session, starting at "connecting"; no
  // socket is "disconnected". Reset during render when that changes — the effect
  // below only runs the session, and its messages move the state on from here.
  const sessionKey = isConnected ? `${deviceId}:${String(attempt)}` : null;
  const [stateFor, setStateFor] = useState<string | null | undefined>(undefined);
  if (sessionKey !== stateFor) {
    setStateFor(sessionKey);
    setConnectionState(sessionKey === null ? 'disconnected' : 'connecting');
    setIdleSecondsLeft(null);
  }

  useEffect(() => {
    if (!containerRef.current) return;

    // The terminal's grid must match the device's, or full-screen programs
    // (minicom, editors) put the cursor in the wrong place. It starts at the
    // size a gateway opens at by default, and takes the size the gateway
    // reports on `proxy_opened`; FitAddon only proposes one.
    const terminal = new Terminal({
      cursorBlink: true,
      cols: DEFAULT_COLS,
      rows: DEFAULT_ROWS,
      fontSize: 14,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Menlo, Monaco, 'Courier New', monospace",
      scrollback: 5000,
      theme: {
        background: '#1e1e2e',
        foreground: '#cdd6f4',
        cursor: '#f5e0dc',
        selectionBackground: '#585b70',
        black: '#45475a',
        red: '#f38ba8',
        green: '#a6e3a1',
        yellow: '#f9e2af',
        blue: '#89b4fa',
        magenta: '#f5c2e7',
        cyan: '#94e2d5',
        white: '#bac2de',
        brightBlack: '#585b70',
        brightRed: '#f38ba8',
        brightGreen: '#a6e3a1',
        brightYellow: '#f9e2af',
        brightBlue: '#89b4fa',
        brightMagenta: '#f5c2e7',
        brightCyan: '#94e2d5',
        brightWhite: '#a6adc8',
      },
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(containerRef.current);

    terminalRef.current = terminal;
    fitAddonRef.current = fitAddon;

    return () => {
      terminal.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
    };
  }, []);

  // Handle connection lifecycle — re-runs on reconnect.
  // Every run opens its own session id. A previous run's session (React strict
  // mode's double effect, or a reconnect) is closed on cleanup, and its late
  // close/error events carry the old id, so they never match this run. That is
  // what lets a failure arriving before any output — "Device not found", an
  // auth failure — be shown instead of leaving the terminal on "Connecting…".
  useEffect(() => {
    if (!isConnected) return;

    const sessionId = crypto.randomUUID();

    // Ask to open as wide as the container, at the terminal's height. The
    // gateway says what it used; the size may change later in the session.
    const proposed = fitAddonRef.current?.proposeDimensions();
    const terminalSize =
      proposed && terminalRef.current && Number.isFinite(proposed.cols) && proposed.cols > 0
        ? { cols: proposed.cols, rows: terminalRef.current.rows }
        : undefined;

    // Subscribe to proxy session. The active context is what the backend checks
    // the terminal permission in; without one it refuses the session.
    sendRaw({
      type: 'subscribe',
      scope: {
        type: 'proxy_session',
        sessionId,
        deviceId,
      },
      contextId: activeContextId,
      terminalSize,
    });

    terminalRef.current?.write('Connecting to device...\r\n');

    // What the gateway's idle close counts: bytes either way. The browser sees
    // them all, so it can warn before the gateway closes the session.
    let lastActivity = Date.now();
    let ended = false;
    const idleTimer = setInterval(() => {
      const left = IDLE_CLOSE_MS - (Date.now() - lastActivity);
      setIdleSecondsLeft(!ended && left <= IDLE_WARNING_MS ? Math.max(0, Math.ceil(left / 1000)) : null);
    }, 1000);
    const endSession = () => {
      ended = true;
      setIdleSecondsLeft(null);
      setConnectionState('disconnected');
    };

    // How the session ended on the gateway: red only for one that failed; one
    // the user ended with `exit` is grey.
    const showEnd = (eventType: unknown, message: unknown) => {
      endSession();
      const reason = typeof message === 'string' && message ? message : 'Session closed.';
      const color = eventType === PROXY_SESSION_ERROR ? '\x1b[31m' : '\x1b[90m';
      terminalRef.current?.write(`\r\n${color}${reason}\x1b[0m\r\n`);
    };

    const showOutput = (data: string) => {
      // Hand xterm the device's bytes; it decodes them as UTF-8.
      try {
        terminalRef.current?.write(decodeOutput(data));
        lastActivity = Date.now();
        setConnectionState('connected');
      } catch {
        console.error('Failed to decode proxy data');
      }
    };

    // On the reliable transport once the gateway opens the session that way
    // (docs/GatewayReliableTransport.md): keystrokes and output arrive once and
    // in order across the gateway's stream rolls.
    let stream: TerminalStream | undefined;

    // Register raw message handler for proxy data
    const removeListener = addRawListener((data: unknown) => {
      if (typeof data !== 'object' || data === null) return false;
      const msg = data as Record<string, unknown>;

      // Handle proxy_data messages
      if (msg.type === 'proxy_data' && msg.sessionId === sessionId) {
        if (typeof msg.data === 'string') showOutput(msg.data);
        return true;
      }

      // A frame of the session's stream, relayed from the gateway.
      if (msg.type === 'stream_frame' && msg.sessionId === sessionId) {
        if (stream && typeof msg.header === 'object' && msg.header !== null) {
          stream.receive(msg.header as StreamHeader, (msg.message ?? null) as TerminalFrameMessage | null);
        }
        return true;
      }

      // The session is open, at the size the gateway opened the device's
      // terminal at; one that doesn't say opened it at the default.
      if (msg.type === 'proxy_opened' && msg.sessionId === sessionId) {
        const size = msg.initialSize as { cols?: unknown; rows?: unknown } | null | undefined;
        if (size && typeof size.cols === 'number' && typeof size.rows === 'number') {
          terminalRef.current?.resize(size.cols, size.rows);
        } else {
          terminalRef.current?.resize(DEFAULT_COLS, DEFAULT_ROWS);
        }
        if (msg.byStream === true && !stream) {
          stream = new TerminalStream({
            sessionId,
            send: (frame) => {
              sendRaw({ ...frame });
            },
            onData: showOutput,
            onEvent: showEnd,
            onLost: () => {
              endSession();
              terminalRef.current?.write('\r\n\x1b[31mThe connection to the device was lost.\x1b[0m\r\n');
            },
          });
        }
        setConnectionState('connected');
        return true;
      }

      // Handle proxy_event messages (session closed, etc.)
      if (msg.type === 'proxy_event' && msg.sessionId === sessionId) {
        showEnd(msg.eventType, msg.message);
        return true;
      }

      // Handle proxy_error messages
      if (msg.type === 'proxy_error' && msg.sessionId === sessionId) {
        endSession();
        const errorText = typeof msg.error === 'string' ? msg.error : 'Unknown error';
        terminalRef.current?.write(`\r\n\x1b[31mError: ${errorText}\x1b[0m\r\n`);
        return true;
      }

      return false;
    });

    // Register terminal input handlers: typed text goes as UTF-8, xterm's
    // binary input (some mouse reports) byte for byte.
    const sendInput = (data: string) => {
      lastActivity = Date.now();
      if (stream) {
        stream.input(data);
        return;
      }
      sendRaw({
        type: 'proxy_data',
        sessionId,
        data,
      });
    };
    const onDataDisposable = terminalRef.current?.onData((data: string) => {
      sendInput(encodeInput(data));
    });
    const onBinaryDisposable = terminalRef.current?.onBinary((data: string) => {
      sendInput(encodeBinaryInput(data));
    });

    return () => {
      // Close the proxy session
      sendRaw({
        type: 'proxy_close',
        sessionId,
      });
      removeListener();
      onDataDisposable?.dispose();
      onBinaryDisposable?.dispose();
      clearInterval(idleTimer);
      stream?.dispose();
    };
  }, [isConnected, deviceId, activeContextId, attempt, sendRaw, addRawListener]);

  // What the last session showed stays above the new one, e.g. what was being
  // done when it dropped.
  const handleReconnect = () => {
    terminalRef.current?.write('\r\n');
    setAttempt((n) => n + 1);
  };

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-[#181825] border-b border-[#313244]">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-block w-2 h-2 rounded-full',
              connectionState === 'connected'
                ? 'bg-[#a6e3a1]'
                : connectionState === 'connecting'
                  ? 'bg-[#f9e2af]'
                  : 'bg-[#f38ba8]',
            )}
          />
          <span className="text-[#cdd6f4] text-sm">
            {connectionState === 'connected'
              ? 'Connected'
              : connectionState === 'connecting'
                ? 'Connecting…'
                : 'Disconnected'}
          </span>
          {connectionState !== 'disconnected' && idleSecondsLeft !== null ? (
            <span className="text-[#f9e2af] text-xs">
              Closing in {idleSecondsLeft}s for inactivity
            </span>
          ) : null}
        </div>
        {connectionState === 'disconnected' ? (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md bg-[#313244] text-[#cdd6f4] px-2 py-1 text-xs font-medium hover:bg-[#45475a]"
            onClick={handleReconnect}
          >
            Reconnect
          </button>
        ) : null}
      </div>

      <div ref={containerRef} className="bg-[#1e1e2e] p-1 overflow-x-auto" />
    </div>
  );
}
